import { useState, useEffect } from 'react';
import { Clock, Save, Calendar, Plus, Trash2, Loader2 } from 'lucide-react';
import { useAdminSettings, useUpdateAdminSettings } from '../hooks/useAdminSettings';
import { useHolidays, useCreateHoliday, useDeleteHoliday } from '../hooks/useHolidays';

export function SettingsPage() {
  const { data: settings, isLoading } = useAdminSettings();
  const updateSettings = useUpdateAdminSettings();

  const [lunchStartTime, setLunchStartTime] = useState('12:00');
  const [lunchDurationMinutes, setLunchDurationMinutes] = useState(30);

  const { data: holidays = [], isLoading: isLoadingHolidays } = useHolidays();
  const createHoliday = useCreateHoliday();
  const deleteHoliday = useDeleteHoliday();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setLunchStartTime(settings.lunchStartTime);
      setLunchDurationMinutes(settings.lunchDurationMinutes);
    }
  }, [settings]);

  const handleSave = () => {
    updateSettings.mutate({ lunchStartTime, lunchDurationMinutes });
  };

  const hasChanges =
    settings &&
    (lunchStartTime !== settings.lunchStartTime ||
      lunchDurationMinutes !== settings.lunchDurationMinutes);

  if (isLoading) {
    return <div className="text-foreground-muted">Loading settings...</div>;
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Settings</h1>

      <div className="bg-background rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-foreground-muted" />
          <h2 className="text-lg font-medium text-foreground">Lunch Break</h2>
        </div>
        <p className="text-sm text-foreground-muted mb-4">
          Company-wide default lunch break. Individual workers can override this in their profile.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={lunchStartTime}
              onChange={(e) => setLunchStartTime(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min={0}
              max={120}
              value={lunchDurationMinutes}
              onChange={(e) => setLunchDurationMinutes(Number(e.target.value))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <p className="mt-1 text-xs text-foreground-muted">0 = no lunch break</p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-foreground-muted">
            Changes trigger a schedule recalculation.
          </p>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateSettings.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            <Save className="h-4 w-4" />
            {updateSettings.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>

        {updateSettings.isSuccess && (
          <p className="mt-2 text-sm text-green-600">Settings saved. Schedule recalculating.</p>
        )}
        {updateSettings.isError && (
          <p className="mt-2 text-sm text-red-600">
            Failed to save settings. Please try again.
          </p>
        )}
      </div>

      {/* Company Holidays */}
      <div className="bg-background rounded-lg border border-border p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-foreground-muted" />
            <h2 className="text-lg font-medium text-foreground">Company Holidays</h2>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Holiday
          </button>
        </div>
        <p className="text-sm text-foreground-muted mb-4">
          Company-wide holidays. All workers are off on these days.
        </p>

        {showAddForm && (
          <div className="mb-4 p-4 border border-border rounded-md bg-background-subtle">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Date</label>
                <input
                  type="date"
                  value={newHolidayDate}
                  onChange={(e) => setNewHolidayDate(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                <input
                  type="text"
                  value={newHolidayName}
                  onChange={(e) => setNewHolidayName(e.target.value)}
                  placeholder="e.g., Independence Day"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (!newHolidayDate || !newHolidayName.trim()) return;
                  createHoliday.mutate(
                    { date: newHolidayDate, name: newHolidayName.trim() },
                    {
                      onSuccess: () => {
                        setNewHolidayDate('');
                        setNewHolidayName('');
                        setShowAddForm(false);
                      },
                    }
                  );
                }}
                disabled={!newHolidayDate || !newHolidayName.trim() || createHoliday.isPending}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {createHoliday.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Add
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewHolidayDate(''); setNewHolidayName(''); }}
                className="px-3 py-1.5 text-sm text-foreground-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
            {createHoliday.isError && (
              <p className="mt-2 text-sm text-red-600">
                {createHoliday.error instanceof Error ? createHoliday.error.message : 'Failed to create holiday'}
              </p>
            )}
          </div>
        )}

        {isLoadingHolidays ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-foreground-muted" />
          </div>
        ) : holidays.length === 0 ? (
          <p className="text-sm text-foreground-muted py-4 text-center">No holidays defined</p>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-foreground-muted">Date</th>
                  <th className="text-left px-4 py-2 font-medium text-foreground-muted">Name</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {holidays.map((holiday) => (
                  <tr key={holiday.id} className="hover:bg-background-subtle/50">
                    <td className="px-4 py-2 font-mono text-foreground">{holiday.date}</td>
                    <td className="px-4 py-2 text-foreground">{holiday.name}</td>
                    <td className="px-4 py-2">
                      {deleteConfirmId === holiday.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              deleteHoliday.mutate(holiday.id, { onSuccess: () => setDeleteConfirmId(null) });
                            }}
                            disabled={deleteHoliday.isPending}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs text-foreground-muted hover:text-foreground"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(holiday.id)}
                          className="p-1 text-foreground-muted hover:text-danger rounded transition-colors"
                          title="Delete holiday"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
